'use client';

import type { CSSProperties } from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import { Icon } from './icon/icon';
import styles from './toaster.module.css';

/**
 * The app's one toast outlet, drawn as falcon-fe's `ToastComponent`
 * (`core/components/toast/`): a white card with a 5px top border and an icon
 * in the type's colour, the message, and a close X — stacked bottom-left,
 * 550px wide, gone after 5s (falcon-fe's `short`, the duration most of its
 * toasts use).
 *
 * Only the chrome is replaced. Screens keep calling `toast.success(...)` /
 * `toast.error(...)` from `sonner` exactly as before — `unstyled` drops
 * sonner's own card, and `classNames`/`icons` put falcon-fe's in its place,
 * so none of those call sites had to change. Mount it once, in
 * `app/providers.tsx`.
 */
export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-left"
      offset={16}
      duration={5000}
      closeButton
      expand
      style={{ '--width': 'min(550px, calc(100vw - 32px))' } as CSSProperties}
      icons={{
        success: (
          <Icon
            name="check-circle"
            size="md"
            color="var(--semantics-text-system-positive, #37B349)"
          />
        ),
        error: (
          <Icon name="circle-x" size="md" color="var(--semantics-text-system-negative, #F45E3F)" />
        ),
        warning: (
          <Icon
            name="alert-triangle"
            size="md"
            color="var(--semantics-text-system-attention, #EAA016)"
          />
        ),
        info: <Icon name="info" size="md" color="var(--semantics-text-brand-secondary, #0062FF)" />,
        close: <Icon name="x" size="sm" label="Close" />,
      }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: styles.toast,
          icon: styles.icon,
          content: styles.content,
          title: styles.title,
          description: styles.description,
          actionButton: styles.actionButton,
          cancelButton: styles.actionButton,
          closeButton: styles.closeButton,
          success: styles.success,
          error: styles.error,
          warning: styles.warning,
          info: styles.info,
        },
      }}
    />
  );
}
